"use client";

import { useSetAtom } from "jotai";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { FiRefreshCw } from "react-icons/fi";
import { v4 as uuidv4 } from "uuid";
import { userDetailsAtom } from "@/atoms/user-details.atoms";
import Button from "@/components/ui/button";
import ErrorMessage from "@/components/ui/error-message";
import InputText from "@/components/ui/input-text";
import Spinner from "@/components/ui/spinner";
import { useToast } from "@/providers/toast.provider";
import { apiClient } from "@/utils/api/api-client";

const OTP_LENGTH = 6;

interface VerifyOtpProps {
  userDetails: {
    identifier: string;
    userId: string;
  };
}

const VerifyOtp: React.FC<VerifyOtpProps> = ({ userDetails }) => {
  const router = useRouter();
  const { showToast } = useToast();
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [otpError, setOtpError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [resendCountdown, setResendCountdown] = useState<number>(60);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const otpKeys = useRef<string[]>(
    Array.from({ length: OTP_LENGTH }, () => uuidv4()),
  );
  const setUserDetails = useSetAtom(userDetailsAtom);

  useEffect(() => {
    if (resendCountdown <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [resendCountdown]);

  const handleChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return;

    const updatedOtp = [...otp];
    updatedOtp[index] = value;
    setOtp(updatedOtp);

    if (updatedOtp.every((digit) => digit)) {
      setOtpError("");
    }

    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      const updatedOtp = [...otp];
      updatedOtp[index] = e.key;
      setOtp(updatedOtp);
      setOtpError("");

      if (index < OTP_LENGTH - 1) {
        inputsRef.current[index + 1]?.focus();
      }
      return;
    }

    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleSubmit = () => {
    const otpValue = otp.join("");
    if (otp.some((digit) => digit === "")) {
      setOtpError(`Please enter the ${OTP_LENGTH}-digit OTP.`);
      return;
    }
    setIsLoading(true);
    apiClient
      .post<
        {
          success: boolean;
          companyName: string;
          userId: string;
          contactPerson: string;
          email: string;
          isAdmin: boolean;
        },
        { userId: string; otp: string }
      >("/auth/verify-otp", {
        userId: userDetails.userId,
        otp: otpValue,
      })
      .then((resp) => {
        if (resp.success) {
          setUserDetails({
            userId: resp.userId,
            companyName: resp.companyName,
            contactPerson: resp.contactPerson,
            email: resp.email,
            isAdmin: resp.isAdmin,
          });
          router.replace(resp.isAdmin ? "/admin/home" : "/home");
        }
      })
      .catch((e: ApiError) => {
        showToast(e?.error.message, {
          type: "error",
        });
        // Temporary change for demo - phone numbers
        // const isPhoneNumber = /^\d{10,15}$/.test(userDetails.identifier);
        // if (isPhoneNumber) router.push("/home");
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      <Spinner show={isLoading} />
      {/* OTP Inputs */}
      <div>
        <div className="flex justify-between gap-1.5">
          {otp.map((digit, index) => (
            <InputText
              key={otpKeys.current[index]}
              name={`otp-${index}`}
              ref={(el) => {
                inputsRef.current[index] = el;
              }}
              autoComplete="one-time-code"
              type="text"
              inputMode="numeric"
              pattern="\d*"
              maxLength={1}
              value={digit}
              onFocus={(e) => e.currentTarget.select()}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className="h-10 w-10 md:h-12 sm:w-12 text-center appearance-none caret-transparent
           [&::-webkit-outer-spin-button]:appearance-none
           [&::-webkit-inner-spin-button]:appearance-none
           [-moz-appearance:textfield]"
            />
          ))}
        </div>
        {otpError ? <ErrorMessage className="mt-2" message={otpError} /> : null}
      </div>

      {/* Submit Button */}
      <Button fullWidth size="lg" onClick={handleSubmit}>
        Submit
      </Button>

      {/* Resend OTP */}
      <Button
        className="text-sm font-normal"
        fullWidth
        variant="link"
        icon={<FiRefreshCw />}
        disabled={isLoading || resendCountdown > 0}
        onClick={() => {
          setResendCountdown(60);
          setIsLoading(true);
          apiClient
            .post<{ success: boolean }, { identifier: string }>(
              "/auth/request-otp",
              {
                identifier: userDetails.identifier,
              },
            )
            .then((resp) => {
              if (resp.success) {
                showToast("OTP resent successfully!", {
                  type: "success",
                });
              } else {
                showToast("Please try again later!", {
                  type: "error",
                });
              }
            })
            .catch((e: ApiError) =>
              showToast(e?.error.message, {
                type: "error",
              }),
            )
            .finally(() => setIsLoading(false));
        }}
      >
        {resendCountdown > 0
          ? `Resend OTP (${resendCountdown}s)`
          : "Resend OTP"}
      </Button>
    </>
  );
};

export default VerifyOtp;
